const BLANK_LABELS = ['blank', 'no engraving', 'no engrave', 'none'];
const MAX_ARTWORK_BYTES = 20 * 1024 * 1024;

const initialize = (container) => {
  if (!(container instanceof HTMLElement) || container.dataset.initialized === 'true') return;

  const form = container.closest('form[action*="/cart/add"]');
  if (!(form instanceof HTMLFormElement)) return;

  const variantInput = form.querySelector('input[name="id"]');
  if (!(variantInput instanceof HTMLInputElement)) return;

  container.dataset.initialized = 'true';
  form.enctype = 'multipart/form-data';

  const variantsNode = container.querySelector('[data-guardian-variants]');
  const variants = variantsNode ? JSON.parse(variantsNode.textContent || '[]') : [];
  const personalizedGroups = container.querySelectorAll('[data-personalized-only]');
  const personalizationInputs = container.querySelectorAll('[data-guardian-personalization-input]');
  const engraving = container.querySelector('textarea[name="properties[Engraving text]"]');
  const artwork = container.querySelector('input[type="file"]');
  const approval = container.querySelector('[data-guardian-approval]');
  const error = container.querySelector('[data-guardian-error]');
  const status = container.querySelector('[data-guardian-status]');
  const characterCount = container.querySelector('[data-guardian-character-count]');

  const personalizationIsValid = () => {
    const enabledInputs = Array.from(personalizationInputs).filter((input) => !input.disabled);
    const hasText = engraving instanceof HTMLTextAreaElement && engraving.value.trim().length > 0;
    const hasArtwork = artwork instanceof HTMLInputElement && (artwork.files?.length || 0) > 0;
    const approved = approval instanceof HTMLInputElement && approval.checked;
    return !enabledInputs.length || ((hasText || hasArtwork) && approved);
  };

  const blockInvalidAdd = (event) => {
    const submitter = event.target instanceof Element ? event.target.closest('[type="submit"]') : null;
    if (!submitter || personalizationIsValid()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (error) error.hidden = false;
    if (approval instanceof HTMLInputElement && !approval.checked) approval.focus();
    else if (engraving instanceof HTMLTextAreaElement) engraving.focus();
  };

  const isBlankVariant = (variant) => {
    const sku = String(variant?.sku || '').toUpperCase();
    const label = Array.isArray(variant?.options) ? variant.options.join(' ').toLowerCase() : '';
    return sku.endsWith('-B') || BLANK_LABELS.some((term) => label.includes(term));
  };

  const refresh = () => {
    const variant = variants.find((item) => String(item.id) === String(variantInput.value));
    const blank = isBlankVariant(variant);
    personalizedGroups.forEach((group) => { group.hidden = blank; });
    personalizationInputs.forEach((input) => { input.disabled = blank; });
    if (approval instanceof HTMLInputElement) {
      approval.disabled = blank;
      approval.required = !blank;
      if (blank) approval.checked = false;
    }
    if (error) error.hidden = true;
    if (status) {
      status.textContent = blank
        ? 'Blank product selected. No artwork or engraving is required.'
        : 'Personalized product selected. Add engraving text, artwork, or both.';
    }
  };

  const updateCharacterCount = () => {
    if (characterCount && engraving instanceof HTMLTextAreaElement) {
      characterCount.textContent = `${engraving.value.length}/${engraving.maxLength}`;
    }
  };

  engraving?.addEventListener('input', updateCharacterCount);
  artwork?.addEventListener('change', () => {
    if (!(artwork instanceof HTMLInputElement)) return;
    const tooLarge = (artwork.files?.[0]?.size || 0) > MAX_ARTWORK_BYTES;
    artwork.setCustomValidity(tooLarge ? 'Artwork must be 20 MB or smaller.' : '');
    if (tooLarge) artwork.reportValidity();
  });

  form.addEventListener('submit', (event) => {
    const enabledInputs = Array.from(personalizationInputs).filter((input) => !input.disabled);
    const hasText = engraving instanceof HTMLTextAreaElement && engraving.value.trim().length > 0;
    const hasArtwork = artwork instanceof HTMLInputElement && (artwork.files?.length || 0) > 0;

    if (enabledInputs.length && !hasText && !hasArtwork) {
      event.preventDefault();
      if (error) error.hidden = false;
      if (engraving instanceof HTMLTextAreaElement) engraving.focus();
    }

    if (artwork instanceof HTMLInputElement && (artwork.files?.[0]?.size || 0) > MAX_ARTWORK_BYTES) {
      event.preventDefault();
      artwork.reportValidity();
    }
  });
  form.addEventListener('click', blockInvalidAdd, true);

  variantInput.addEventListener('change', refresh);
  new MutationObserver(refresh).observe(variantInput, { attributes: true, attributeFilter: ['value'] });
  refresh();
  updateCharacterCount();
};

const initializeAll = (root = document) => {
  root.querySelectorAll('[data-guardian-personalization]').forEach(initialize);
};

initializeAll();
document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.matches('[data-guardian-personalization]')) initialize(node);
      initializeAll(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
